export interface QueryStringOptions {
    // 기본값들을 정의해서 해당 값이면 쿼리에 포함하지 않음
    defaultValues?: Record<string, any>;
    // 특정 필드를 제외하고 싶을 때
    excludeKeys?: string[];
    // 빈 값 처리 방식
    includeEmpty?: boolean;
}

/**
 * 객체를 쿼리스트링으로 변환하는 범용 함수
 * @param obj 변환할 객체
 * @param options 옵션 설정
 * @returns 쿼리스트링 (앞에 ? 포함)
 */
export const objectToQueryString = (
    obj: Record<string, any>,
    options: QueryStringOptions = {},
): string => {
    const {
        defaultValues = {},
        excludeKeys = [],
        includeEmpty = false,
    } = options;

    const queryParams = new URLSearchParams();

    Object.entries(obj).forEach(([key, value]) => {
        // 제외할 키면 스킵
        if (excludeKeys.includes(key)) return;

        // undefined나 null이면 스킵
        if (value === undefined || value === null) return;

        // 빈 값 처리
        if (!includeEmpty && value === '') return;

        // 기본값과 같으면 스킵
        if (defaultValues[key] !== undefined && value === defaultValues[key])
            return;

        // 배열인 경우 처리
        if (Array.isArray(value)) {
            value.forEach(item => queryParams.append(key, String(item)));
        } else {
            queryParams.set(key, String(value));
        }
    });

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
};

/**
 * 쿼리스트링을 객체로 변환하는 함수
 * @param searchParams URLSearchParams 객체
 * @param typeConverters 타입 변환 함수들
 * @returns 파싱된 객체
 */
export const queryStringToObject = <T extends Record<string, any>>(
    searchParams: URLSearchParams,
    typeConverters: Partial<Record<keyof T, (value: string) => any>> = {},
): Partial<T> => {
    const result: Partial<T> = {};

    searchParams.forEach((value, key) => {
        const converter = typeConverters[key as keyof T];
        (result as any)[key] = converter ? converter(value) : value;
    });

    return result;
};

// 자주 사용되는 타입 변환기들
// 자주 사용되는 타입 변환기들
export const typeConverters = {
    number: (value: string) => parseInt(value, 10),
    float: (value: string) => parseFloat(value),
    boolean: (value: string) => value === 'true',
    date: (value: string) => new Date(value),
};

// 날짜 문자열을 Date 객체로 변환 (YYYY-MM-DD 형식)
export const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
};

// 날짜를 YYYY-MM-DD 형식으로 변환
export const formatDateString = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
